import React from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useParameterizedQuery } from 'react-fetching-library';

import { createTransactionAction } from '../../../services/request';
import PaySpecificAmountModal from '../../client/settlements/PaySpecificAmountModal';
import { useClientZS } from '../../../pages/ClientZS';

export const PaymentSummaryCard = () => {
  const { orderData, purchaserData, paid, advanceLeftToPay } = useClientZS();
  const { query: transactionQuery } = useParameterizedQuery(createTransactionAction);
  const [openPayModal, setOpenPayModal] = React.useState(false);

  const paymentState =
    orderData.left_to_pay === 0
      ? 'Zapłacono'
      : orderData.sum_of_advances > 0
        ? 'Zaliczka opłacona'
        : 'Do zapłaty';

  const paymentColor =
    orderData.left_to_pay === 0
      ? 'success'
      : orderData.sum_of_advances > 0
        ? 'warning'
        : 'error';

  const formatCurrency = value => new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN'
  }).format(Number(value || 0));

  const handlePay = async (to_pay = null) => {
    const data = {
      order_id: orderData.id,
      to_pay: !to_pay ? orderData.left_to_pay : to_pay,
      contract_number: orderData.agreement_number === '' ? null : orderData.agreement_number,
      email: purchaserData.correspondence_email,
      name: purchaserData.name,
      purchaser_id: purchaserData.id,
      participant: ''
    };
    const params = new URLSearchParams(data).toString();
    const { payload, error, loading } = await transactionQuery(params);
    if (!error && !loading) {
      window.open(payload.url, '_blank');
    }

    if (error) {
      toast.error('Wystąpił błąd');
    }
  };

  const handleClosePayModal = () => setOpenPayModal(false);

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: theme => theme.shadows[2]
      }}
    >
      <CardContent sx={{ p: 5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          mb={4}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PaymentsOutlinedIcon color="primary" />
          </Box>

          <Typography
            variant="h4"
            fontWeight={700}
          >
            Podsumowanie płatności
          </Typography>
        </Stack>

        <Chip
          color={paymentColor}
          label={paymentState}
          sx={{
            mb: 5,
            fontWeight: 600
          }}
        />

        <Stack spacing={4}>
          <Box>
            <Typography color="text.secondary">
              Wartość zamówienia
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
            >
              {formatCurrency(
                orderData.order_value
              )}
            </Typography>
          </Box>

          <Box>
            <Typography color="text.secondary">
              Zapłacono
            </Typography>

            <Typography
              variant="h4"
              fontWeight={700}
              color="success.main"
            >
              {formatCurrency(paid)}
            </Typography>
          </Box>

          {orderData.sum_of_advances > 0 && (
            <Box>
              <Typography color="text.secondary">
                Wpłacone zaliczki
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
              >
                {formatCurrency(
                  orderData.sum_of_advances
                )}
              </Typography>
            </Box>
          )}

          {advanceLeftToPay > 0 && (
            <Box>
              <Typography color="text.secondary">
                Pozostała zaliczka do zapłaty
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                color="error.main"
              >
                {formatCurrency(advanceLeftToPay)}
              </Typography>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography color="text.secondary">
              Pozostało do zapłaty
            </Typography>

            <Typography
              variant="h3"
              fontWeight={700}
              color={
                orderData.left_to_pay > 0
                  ? 'error.main'
                  : 'success.main'
              }
            >
              {formatCurrency(
                orderData.left_to_pay
              )}
            </Typography>
          </Box>

          {orderData.state === 'CON' && (
            <>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => handlePay(advanceLeftToPay)}
                disabled={advanceLeftToPay <= 0}
              >
                Zapłać zaliczkę
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => handlePay()}
                disabled={orderData.left_to_pay <= 0}
              >
                Zapłać całość
              </Button>

              <Button
                fullWidth
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                onClick={() => setOpenPayModal(true)}
                disabled={orderData.left_to_pay <= 0}
              >
                Zapłać konkretną kwotę
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
      <PaySpecificAmountModal open={openPayModal} handleClose={handleClosePayModal} order={orderData} orderQuery={() => {}} />
    </Card>
  );
};
